/**
 * Generated by orval
 * Do not edit manually.
 * IP Fabric API
 * IP Fabric API documentation. For details see [IP Fabric API documentation](https://docs.ipfabric.io/main/IP_Fabric_API/)
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import type {
  CreateLabelRuleBody,
  DeleteLabelsSnapshotsSnapshotIdAssignmentsIdParams,
  GetLabels200Item,
  GetLabelsAssignmentTargetsParams,
  GetLabelsRbacEnrolment200Item,
  GetLabelsRbacEnrolmentParams,
  PostLabels200,
  PostLabelsAssignmentsBody,
  PostLabelsAssignmentsUnassignBody,
  PostLabelsBody,
  PostTablesLabelSnapshotNames200,
  PostTablesLabelSnapshotNamesBody,
  PostTablesLabelSnapshotPairs200,
  PostTablesLabelSnapshotPairsBody,
  PostTablesLabels200,
  PostTablesLabelsBody,
  PutLabelsId200,
  PutLabelsIdBody,
  PutLabelsRbacEnrolmentBody,
} from '../../schemas';

import { apiClient } from '../../../apiClient';

/**
 * Lists label catalog entries used for label autocomplete and presets.
 * @summary List label catalog entries
 */
export const getLabels = (signal?: AbortSignal) => {
  return apiClient<GetLabels200Item[]>({
    url: `/labels`,
    method: 'GET',
    signal,
  });
};

export const getGetLabelsQueryKey = () => {
  return [`/labels`] as const;
};

export const getGetLabelsQueryOptions = <
  TData = Awaited<ReturnType<typeof getLabels>>,
  TError = unknown,
>(options?: {
  query?: Partial<
    UseQueryOptions<Awaited<ReturnType<typeof getLabels>>, TError, TData>
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetLabelsQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getLabels>>> = ({
    signal,
  }) => getLabels(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getLabels>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetLabelsQueryResult = NonNullable<
  Awaited<ReturnType<typeof getLabels>>
>;
export type GetLabelsQueryError = unknown;

export function useGetLabels<
  TData = Awaited<ReturnType<typeof getLabels>>,
  TError = unknown,
>(
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getLabels>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getLabels>>,
          TError,
          Awaited<ReturnType<typeof getLabels>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetLabels<
  TData = Awaited<ReturnType<typeof getLabels>>,
  TError = unknown,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getLabels>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getLabels>>,
          TError,
          Awaited<ReturnType<typeof getLabels>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetLabels<
  TData = Awaited<ReturnType<typeof getLabels>>,
  TError = unknown,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getLabels>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary List label catalog entries
 */

export function useGetLabels<
  TData = Awaited<ReturnType<typeof getLabels>>,
  TError = unknown,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getLabels>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetLabelsQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Creates or replaces a label catalog entry identified by its name, used for label autocomplete and presets.
Label names starting with `ipf_` are reserved. Only `ipf_site`, `ipf_routingDomain`, `ipf_stpDomain` and `ipf_region` are accepted, and reserved catalog entries cannot be renamed or deleted. IP Fabric computes `ipf_site`, `ipf_routingDomain` and `ipf_stpDomain` (assignment source `ipf`); a manual or auto assignment overrides the computed value. `ipf_region` is never computed: its value is required and must match `^[A-Za-z0-9 _.-]{1,64}$`. A violation returns 422 `API_VALIDATION_FAILED`.
Replacing the values of a label enrolled for RBAC is rejected when a data policy still uses a removed value.
 * @summary Create label catalog entry
 */
export const postLabels = (
  postLabelsBody: PostLabelsBody,
  signal?: AbortSignal,
) => {
  return apiClient<PostLabels200>({
    url: `/labels`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: postLabelsBody,
    signal,
  });
};

export const getPostLabelsMutationOptions = <
  TError = unknown,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postLabels>>,
    TError,
    { data: PostLabelsBody },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postLabels>>,
  TError,
  { data: PostLabelsBody },
  TContext
> => {
  const mutationKey = ['postLabels'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postLabels>>,
    { data: PostLabelsBody }
  > = (props) => {
    const { data } = props ?? {};

    return postLabels(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostLabelsMutationResult = NonNullable<
  Awaited<ReturnType<typeof postLabels>>
>;
export type PostLabelsMutationBody = PostLabelsBody;
export type PostLabelsMutationError = unknown;

/**
 * @summary Create label catalog entry
 */
export const usePostLabels = <TError = unknown, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof postLabels>>,
      TError,
      { data: PostLabelsBody },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof postLabels>>,
  TError,
  { data: PostLabelsBody },
  TContext
> => {
  const mutationOptions = getPostLabelsMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Updates the label catalog entry with the given id.
Label names starting with `ipf_` are reserved. Only `ipf_site`, `ipf_routingDomain`, `ipf_stpDomain` and `ipf_region` are accepted, and reserved catalog entries cannot be renamed or deleted. IP Fabric computes `ipf_site`, `ipf_routingDomain` and `ipf_stpDomain` (assignment source `ipf`); a manual or auto assignment overrides the computed value. `ipf_region` is never computed: its value is required and must match `^[A-Za-z0-9 _.-]{1,64}$`. A violation returns 422 `API_VALIDATION_FAILED`.
 * @summary Update label catalog entry
 */
export const putLabelsId = (id: string, putLabelsIdBody: PutLabelsIdBody) => {
  return apiClient<PutLabelsId200>({
    url: `/labels/${id}`,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    data: putLabelsIdBody,
  });
};

export const getPutLabelsIdMutationOptions = <
  TError = unknown,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putLabelsId>>,
    TError,
    { id: string; data: PutLabelsIdBody },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putLabelsId>>,
  TError,
  { id: string; data: PutLabelsIdBody },
  TContext
> => {
  const mutationKey = ['putLabelsId'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putLabelsId>>,
    { id: string; data: PutLabelsIdBody }
  > = (props) => {
    const { id, data } = props ?? {};

    return putLabelsId(id, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutLabelsIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof putLabelsId>>
>;
export type PutLabelsIdMutationBody = PutLabelsIdBody;
export type PutLabelsIdMutationError = unknown;

/**
 * @summary Update label catalog entry
 */
export const usePutLabelsId = <TError = unknown, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof putLabelsId>>,
      TError,
      { id: string; data: PutLabelsIdBody },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof putLabelsId>>,
  TError,
  { id: string; data: PutLabelsIdBody },
  TContext
> => {
  const mutationOptions = getPutLabelsIdMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Deletes the label catalog entry with the given id.
 * @summary Delete label catalog entry
 */
export const deleteLabelsId = (id: string) => {
  return apiClient<unknown>({ url: `/labels/${id}`, method: 'DELETE' });
};

export const getDeleteLabelsIdMutationOptions = <
  TError = unknown,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteLabelsId>>,
    TError,
    { id: string },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteLabelsId>>,
  TError,
  { id: string },
  TContext
> => {
  const mutationKey = ['deleteLabelsId'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteLabelsId>>,
    { id: string }
  > = (props) => {
    const { id } = props ?? {};

    return deleteLabelsId(id);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteLabelsIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteLabelsId>>
>;

export type DeleteLabelsIdMutationError = unknown;

/**
 * @summary Delete label catalog entry
 */
export const useDeleteLabelsId = <TError = unknown, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof deleteLabelsId>>,
      TError,
      { id: string },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof deleteLabelsId>>,
  TError,
  { id: string },
  TContext
> => {
  const mutationOptions = getDeleteLabelsIdMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Searches devices and interfaces across all loaded snapshots, deduplicated by stable identity, for use as label assignment targets.
 * @summary List label assignment targets
 */
export const getLabelsAssignmentTargets = (
  params: GetLabelsAssignmentTargetsParams,
  signal?: AbortSignal,
) => {
  return apiClient<unknown>({
    url: `/labels/assignment-targets`,
    method: 'GET',
    params,
    signal,
  });
};

export const getGetLabelsAssignmentTargetsQueryKey = (
  params: GetLabelsAssignmentTargetsParams,
) => {
  return [`/labels/assignment-targets`, ...(params ? [params] : [])] as const;
};

export const getGetLabelsAssignmentTargetsQueryOptions = <
  TData = Awaited<ReturnType<typeof getLabelsAssignmentTargets>>,
  TError = unknown,
>(
  params: GetLabelsAssignmentTargetsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getLabelsAssignmentTargets>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetLabelsAssignmentTargetsQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getLabelsAssignmentTargets>>
  > = ({ signal }) => getLabelsAssignmentTargets(params, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getLabelsAssignmentTargets>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetLabelsAssignmentTargetsQueryResult = NonNullable<
  Awaited<ReturnType<typeof getLabelsAssignmentTargets>>
>;
export type GetLabelsAssignmentTargetsQueryError = unknown;

export function useGetLabelsAssignmentTargets<
  TData = Awaited<ReturnType<typeof getLabelsAssignmentTargets>>,
  TError = unknown,
>(
  params: GetLabelsAssignmentTargetsParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getLabelsAssignmentTargets>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getLabelsAssignmentTargets>>,
          TError,
          Awaited<ReturnType<typeof getLabelsAssignmentTargets>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetLabelsAssignmentTargets<
  TData = Awaited<ReturnType<typeof getLabelsAssignmentTargets>>,
  TError = unknown,
>(
  params: GetLabelsAssignmentTargetsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getLabelsAssignmentTargets>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getLabelsAssignmentTargets>>,
          TError,
          Awaited<ReturnType<typeof getLabelsAssignmentTargets>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetLabelsAssignmentTargets<
  TData = Awaited<ReturnType<typeof getLabelsAssignmentTargets>>,
  TError = unknown,
>(
  params: GetLabelsAssignmentTargetsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getLabelsAssignmentTargets>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary List label assignment targets
 */

export function useGetLabelsAssignmentTargets<
  TData = Awaited<ReturnType<typeof getLabelsAssignmentTargets>>,
  TError = unknown,
>(
  params: GetLabelsAssignmentTargetsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getLabelsAssignmentTargets>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetLabelsAssignmentTargetsQueryOptions(
    params,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Assigns one or more labels to multiple device and interface targets in a single request. The change always applies to the request's snapshot. Set optional body field `reflectToRules` to also upsert manual rules so the assignment survives label recalculation on future snapshots; omit or set false to keep assignments snapshot-local (`ruleId` null).
Label names starting with `ipf_` are reserved. Only `ipf_site`, `ipf_routingDomain`, `ipf_stpDomain` and `ipf_region` are accepted, and reserved catalog entries cannot be renamed or deleted. IP Fabric computes `ipf_site`, `ipf_routingDomain` and `ipf_stpDomain` (assignment source `ipf`); a manual or auto assignment overrides the computed value. `ipf_region` is never computed: its value is required and must match `^[A-Za-z0-9 _.-]{1,64}$`. A violation returns 422 `API_VALIDATION_FAILED`.
 * @summary Create bulk label assignments
 */
export const postLabelsAssignments = (
  postLabelsAssignmentsBody: PostLabelsAssignmentsBody,
  signal?: AbortSignal,
) => {
  return apiClient<unknown>({
    url: `/labels/assignments`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: postLabelsAssignmentsBody,
    signal,
  });
};

export const getPostLabelsAssignmentsMutationOptions = <
  TError = unknown,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postLabelsAssignments>>,
    TError,
    { data: PostLabelsAssignmentsBody },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postLabelsAssignments>>,
  TError,
  { data: PostLabelsAssignmentsBody },
  TContext
> => {
  const mutationKey = ['postLabelsAssignments'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postLabelsAssignments>>,
    { data: PostLabelsAssignmentsBody }
  > = (props) => {
    const { data } = props ?? {};

    return postLabelsAssignments(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostLabelsAssignmentsMutationResult = NonNullable<
  Awaited<ReturnType<typeof postLabelsAssignments>>
>;
export type PostLabelsAssignmentsMutationBody = PostLabelsAssignmentsBody;
export type PostLabelsAssignmentsMutationError = unknown;

/**
 * @summary Create bulk label assignments
 */
export const usePostLabelsAssignments = <TError = unknown, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof postLabelsAssignments>>,
      TError,
      { data: PostLabelsAssignmentsBody },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof postLabelsAssignments>>,
  TError,
  { data: PostLabelsAssignmentsBody },
  TContext
> => {
  const mutationOptions = getPostLabelsAssignmentsMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Removes one or more labels from multiple device and interface targets in a single request. The change always applies to the request's snapshot. Set optional body field `reflectToRules` to also delete the backing manual rules; omit or set false to remove only snapshot assignment rows.
 * @summary Delete bulk label assignments
 */
export const postLabelsAssignmentsUnassign = (
  postLabelsAssignmentsUnassignBody: PostLabelsAssignmentsUnassignBody,
  signal?: AbortSignal,
) => {
  return apiClient<unknown>({
    url: `/labels/assignments/unassign`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: postLabelsAssignmentsUnassignBody,
    signal,
  });
};

export const getPostLabelsAssignmentsUnassignMutationOptions = <
  TError = unknown,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postLabelsAssignmentsUnassign>>,
    TError,
    { data: PostLabelsAssignmentsUnassignBody },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postLabelsAssignmentsUnassign>>,
  TError,
  { data: PostLabelsAssignmentsUnassignBody },
  TContext
> => {
  const mutationKey = ['postLabelsAssignmentsUnassign'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postLabelsAssignmentsUnassign>>,
    { data: PostLabelsAssignmentsUnassignBody }
  > = (props) => {
    const { data } = props ?? {};

    return postLabelsAssignmentsUnassign(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostLabelsAssignmentsUnassignMutationResult = NonNullable<
  Awaited<ReturnType<typeof postLabelsAssignmentsUnassign>>
>;
export type PostLabelsAssignmentsUnassignMutationBody =
  PostLabelsAssignmentsUnassignBody;
export type PostLabelsAssignmentsUnassignMutationError = unknown;

/**
 * @summary Delete bulk label assignments
 */
export const usePostLabelsAssignmentsUnassign = <
  TError = unknown,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof postLabelsAssignmentsUnassign>>,
      TError,
      { data: PostLabelsAssignmentsUnassignBody },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof postLabelsAssignmentsUnassign>>,
  TError,
  { data: PostLabelsAssignmentsUnassignBody },
  TContext
> => {
  const mutationOptions =
    getPostLabelsAssignmentsUnassignMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Lists labels with their RBAC enrolment state, labelled entity count in a snapshot and referencing data policies.
 * @summary List label RBAC enrolment
 */
export const getLabelsRbacEnrolment = (
  params: GetLabelsRbacEnrolmentParams,
  signal?: AbortSignal,
) => {
  return apiClient<GetLabelsRbacEnrolment200Item[]>({
    url: `/labels/rbac-enrolment`,
    method: 'GET',
    params,
    signal,
  });
};

export const getGetLabelsRbacEnrolmentQueryKey = (
  params: GetLabelsRbacEnrolmentParams,
) => {
  return [`/labels/rbac-enrolment`, ...(params ? [params] : [])] as const;
};

export const getGetLabelsRbacEnrolmentQueryOptions = <
  TData = Awaited<ReturnType<typeof getLabelsRbacEnrolment>>,
  TError = unknown,
>(
  params: GetLabelsRbacEnrolmentParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getLabelsRbacEnrolment>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetLabelsRbacEnrolmentQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getLabelsRbacEnrolment>>
  > = ({ signal }) => getLabelsRbacEnrolment(params, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getLabelsRbacEnrolment>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetLabelsRbacEnrolmentQueryResult = NonNullable<
  Awaited<ReturnType<typeof getLabelsRbacEnrolment>>
>;
export type GetLabelsRbacEnrolmentQueryError = unknown;

export function useGetLabelsRbacEnrolment<
  TData = Awaited<ReturnType<typeof getLabelsRbacEnrolment>>,
  TError = unknown,
>(
  params: GetLabelsRbacEnrolmentParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getLabelsRbacEnrolment>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getLabelsRbacEnrolment>>,
          TError,
          Awaited<ReturnType<typeof getLabelsRbacEnrolment>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetLabelsRbacEnrolment<
  TData = Awaited<ReturnType<typeof getLabelsRbacEnrolment>>,
  TError = unknown,
>(
  params: GetLabelsRbacEnrolmentParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getLabelsRbacEnrolment>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getLabelsRbacEnrolment>>,
          TError,
          Awaited<ReturnType<typeof getLabelsRbacEnrolment>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetLabelsRbacEnrolment<
  TData = Awaited<ReturnType<typeof getLabelsRbacEnrolment>>,
  TError = unknown,
>(
  params: GetLabelsRbacEnrolmentParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getLabelsRbacEnrolment>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary List label RBAC enrolment
 */

export function useGetLabelsRbacEnrolment<
  TData = Awaited<ReturnType<typeof getLabelsRbacEnrolment>>,
  TError = unknown,
>(
  params: GetLabelsRbacEnrolmentParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getLabelsRbacEnrolment>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetLabelsRbacEnrolmentQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Enrols and un-enrols the listed labels for RBAC; labels not listed are left unchanged. A label used by a data policy cannot be un-enrolled; remove it from those policies first.
 * @summary Set label RBAC enrolment
 */
export const putLabelsRbacEnrolment = (
  putLabelsRbacEnrolmentBody: PutLabelsRbacEnrolmentBody,
) => {
  return apiClient<unknown>({
    url: `/labels/rbac-enrolment`,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    data: putLabelsRbacEnrolmentBody,
  });
};

export const getPutLabelsRbacEnrolmentMutationOptions = <
  TError = unknown,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putLabelsRbacEnrolment>>,
    TError,
    { data: PutLabelsRbacEnrolmentBody },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putLabelsRbacEnrolment>>,
  TError,
  { data: PutLabelsRbacEnrolmentBody },
  TContext
> => {
  const mutationKey = ['putLabelsRbacEnrolment'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putLabelsRbacEnrolment>>,
    { data: PutLabelsRbacEnrolmentBody }
  > = (props) => {
    const { data } = props ?? {};

    return putLabelsRbacEnrolment(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutLabelsRbacEnrolmentMutationResult = NonNullable<
  Awaited<ReturnType<typeof putLabelsRbacEnrolment>>
>;
export type PutLabelsRbacEnrolmentMutationBody = PutLabelsRbacEnrolmentBody;
export type PutLabelsRbacEnrolmentMutationError = unknown;

/**
 * @summary Set label RBAC enrolment
 */
export const usePutLabelsRbacEnrolment = <TError = unknown, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof putLabelsRbacEnrolment>>,
      TError,
      { data: PutLabelsRbacEnrolmentBody },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof putLabelsRbacEnrolment>>,
  TError,
  { data: PutLabelsRbacEnrolmentBody },
  TContext
> => {
  const mutationOptions = getPutLabelsRbacEnrolmentMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Returns all auto-assignment rules.
 * @summary List auto-assignment rules
 */
export const getLabelRules = (signal?: AbortSignal) => {
  return apiClient<unknown>({ url: `/labels/rules`, method: 'GET', signal });
};

export const getGetLabelRulesQueryKey = () => {
  return [`/labels/rules`] as const;
};

export const getGetLabelRulesQueryOptions = <
  TData = Awaited<ReturnType<typeof getLabelRules>>,
  TError = unknown,
>(options?: {
  query?: Partial<
    UseQueryOptions<Awaited<ReturnType<typeof getLabelRules>>, TError, TData>
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetLabelRulesQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getLabelRules>>> = ({
    signal,
  }) => getLabelRules(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getLabelRules>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetLabelRulesQueryResult = NonNullable<
  Awaited<ReturnType<typeof getLabelRules>>
>;
export type GetLabelRulesQueryError = unknown;

export function useGetLabelRules<
  TData = Awaited<ReturnType<typeof getLabelRules>>,
  TError = unknown,
>(
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getLabelRules>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getLabelRules>>,
          TError,
          Awaited<ReturnType<typeof getLabelRules>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetLabelRules<
  TData = Awaited<ReturnType<typeof getLabelRules>>,
  TError = unknown,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getLabelRules>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getLabelRules>>,
          TError,
          Awaited<ReturnType<typeof getLabelRules>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetLabelRules<
  TData = Awaited<ReturnType<typeof getLabelRules>>,
  TError = unknown,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getLabelRules>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary List auto-assignment rules
 */

export function useGetLabelRules<
  TData = Awaited<ReturnType<typeof getLabelRules>>,
  TError = unknown,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getLabelRules>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetLabelRulesQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Creates a new auto-assignment rule that matches devices or L2 interfaces with an API-table filter and assigns explicit labels to matching targets.
Label names starting with `ipf_` are reserved. Only `ipf_site`, `ipf_routingDomain`, `ipf_stpDomain` and `ipf_region` are accepted, and reserved catalog entries cannot be renamed or deleted. IP Fabric computes `ipf_site`, `ipf_routingDomain` and `ipf_stpDomain` (assignment source `ipf`); a manual or auto assignment overrides the computed value. `ipf_region` is never computed: its value is required and must match `^[A-Za-z0-9 _.-]{1,64}$`. A violation returns 422 `API_VALIDATION_FAILED`.
 * @summary Create auto-assignment rule
 */
export const createLabelRule = (
  createLabelRuleBody: CreateLabelRuleBody,
  signal?: AbortSignal,
) => {
  return apiClient<unknown>({
    url: `/labels/rules`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: createLabelRuleBody,
    signal,
  });
};

export const getCreateLabelRuleMutationOptions = <
  TError = unknown,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createLabelRule>>,
    TError,
    { data: CreateLabelRuleBody },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createLabelRule>>,
  TError,
  { data: CreateLabelRuleBody },
  TContext
> => {
  const mutationKey = ['createLabelRule'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createLabelRule>>,
    { data: CreateLabelRuleBody }
  > = (props) => {
    const { data } = props ?? {};

    return createLabelRule(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateLabelRuleMutationResult = NonNullable<
  Awaited<ReturnType<typeof createLabelRule>>
>;
export type CreateLabelRuleMutationBody = CreateLabelRuleBody;
export type CreateLabelRuleMutationError = unknown;

/**
 * @summary Create auto-assignment rule
 */
export const useCreateLabelRule = <TError = unknown, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createLabelRule>>,
      TError,
      { data: CreateLabelRuleBody },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof createLabelRule>>,
  TError,
  { data: CreateLabelRuleBody },
  TContext
> => {
  const mutationOptions = getCreateLabelRuleMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Returns a single auto-assignment rule by its identifier.
 * @summary Retrieve auto-assignment rule
 */
export const getLabelRule = (id: string, signal?: AbortSignal) => {
  return apiClient<unknown>({
    url: `/labels/rules/${id}`,
    method: 'GET',
    signal,
  });
};

export const getGetLabelRuleQueryKey = (id: string) => {
  return [`/labels/rules/${id}`] as const;
};

export const getGetLabelRuleQueryOptions = <
  TData = Awaited<ReturnType<typeof getLabelRule>>,
  TError = unknown,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getLabelRule>>, TError, TData>
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetLabelRuleQueryKey(id);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getLabelRule>>> = ({
    signal,
  }) => getLabelRule(id, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!id,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getLabelRule>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetLabelRuleQueryResult = NonNullable<
  Awaited<ReturnType<typeof getLabelRule>>
>;
export type GetLabelRuleQueryError = unknown;

export function useGetLabelRule<
  TData = Awaited<ReturnType<typeof getLabelRule>>,
  TError = unknown,
>(
  id: string,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getLabelRule>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getLabelRule>>,
          TError,
          Awaited<ReturnType<typeof getLabelRule>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetLabelRule<
  TData = Awaited<ReturnType<typeof getLabelRule>>,
  TError = unknown,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getLabelRule>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getLabelRule>>,
          TError,
          Awaited<ReturnType<typeof getLabelRule>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetLabelRule<
  TData = Awaited<ReturnType<typeof getLabelRule>>,
  TError = unknown,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getLabelRule>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Retrieve auto-assignment rule
 */

export function useGetLabelRule<
  TData = Awaited<ReturnType<typeof getLabelRule>>,
  TError = unknown,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getLabelRule>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetLabelRuleQueryOptions(id, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Updates an existing auto-assignment rule. Changes take effect for future snapshots only.
Label names starting with `ipf_` are reserved. Only `ipf_site`, `ipf_routingDomain`, `ipf_stpDomain` and `ipf_region` are accepted, and reserved catalog entries cannot be renamed or deleted. IP Fabric computes `ipf_site`, `ipf_routingDomain` and `ipf_stpDomain` (assignment source `ipf`); a manual or auto assignment overrides the computed value. `ipf_region` is never computed: its value is required and must match `^[A-Za-z0-9 _.-]{1,64}$`. A violation returns 422 `API_VALIDATION_FAILED`.
 * @summary Update auto-assignment rule
 */
export const updateLabelRule = (id: string) => {
  return apiClient<unknown>({ url: `/labels/rules/${id}`, method: 'PUT' });
};

export const getUpdateLabelRuleMutationOptions = <
  TError = unknown,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateLabelRule>>,
    TError,
    { id: string },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateLabelRule>>,
  TError,
  { id: string },
  TContext
> => {
  const mutationKey = ['updateLabelRule'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateLabelRule>>,
    { id: string }
  > = (props) => {
    const { id } = props ?? {};

    return updateLabelRule(id);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateLabelRuleMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateLabelRule>>
>;

export type UpdateLabelRuleMutationError = unknown;

/**
 * @summary Update auto-assignment rule
 */
export const useUpdateLabelRule = <TError = unknown, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof updateLabelRule>>,
      TError,
      { id: string },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof updateLabelRule>>,
  TError,
  { id: string },
  TContext
> => {
  const mutationOptions = getUpdateLabelRuleMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Deletes an auto-assignment rule. Existing snapshot labels produced by the rule are not retroactively removed.
 * @summary Delete auto-assignment rule
 */
export const deleteLabelRule = (id: string) => {
  return apiClient<unknown>({ url: `/labels/rules/${id}`, method: 'DELETE' });
};

export const getDeleteLabelRuleMutationOptions = <
  TError = unknown,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteLabelRule>>,
    TError,
    { id: string },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteLabelRule>>,
  TError,
  { id: string },
  TContext
> => {
  const mutationKey = ['deleteLabelRule'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteLabelRule>>,
    { id: string }
  > = (props) => {
    const { id } = props ?? {};

    return deleteLabelRule(id);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteLabelRuleMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteLabelRule>>
>;

export type DeleteLabelRuleMutationError = unknown;

/**
 * @summary Delete auto-assignment rule
 */
export const useDeleteLabelRule = <TError = unknown, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof deleteLabelRule>>,
      TError,
      { id: string },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof deleteLabelRule>>,
  TError,
  { id: string },
  TContext
> => {
  const mutationOptions = getDeleteLabelRuleMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Relabels a manually-assigned label with the given id in the snapshot. Only the label text can change, and only labels whose assignment is manual can be edited here; auto and inherited labels return 404. The relabel always applies to the current snapshot. Set optional body field `reflectToRules` to also update the backing manual rule when the row is linked (`ruleId` set), or to create and link a manual rule when the row is snapshot-only; omit or set false to keep the change snapshot-local (linked rows detach).
Label names starting with `ipf_` are reserved. Only `ipf_site`, `ipf_routingDomain`, `ipf_stpDomain` and `ipf_region` are accepted, and reserved catalog entries cannot be renamed or deleted. IP Fabric computes `ipf_site`, `ipf_routingDomain` and `ipf_stpDomain` (assignment source `ipf`); a manual or auto assignment overrides the computed value. `ipf_region` is never computed: its value is required and must match `^[A-Za-z0-9 _.-]{1,64}$`. A violation returns 422 `API_VALIDATION_FAILED`.
 * @summary Update snapshot label assignment
 */
export const putLabelsSnapshotsSnapshotIdAssignmentsId = (
  snapshotId: string,
  id: string,
) => {
  return apiClient<unknown>({
    url: `/labels/snapshots/${snapshotId}/assignments/${id}`,
    method: 'PUT',
  });
};

export const getPutLabelsSnapshotsSnapshotIdAssignmentsIdMutationOptions = <
  TError = unknown,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putLabelsSnapshotsSnapshotIdAssignmentsId>>,
    TError,
    { snapshotId: string; id: string },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putLabelsSnapshotsSnapshotIdAssignmentsId>>,
  TError,
  { snapshotId: string; id: string },
  TContext
> => {
  const mutationKey = ['putLabelsSnapshotsSnapshotIdAssignmentsId'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putLabelsSnapshotsSnapshotIdAssignmentsId>>,
    { snapshotId: string; id: string }
  > = (props) => {
    const { snapshotId, id } = props ?? {};

    return putLabelsSnapshotsSnapshotIdAssignmentsId(snapshotId, id);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutLabelsSnapshotsSnapshotIdAssignmentsIdMutationResult =
  NonNullable<
    Awaited<ReturnType<typeof putLabelsSnapshotsSnapshotIdAssignmentsId>>
  >;

export type PutLabelsSnapshotsSnapshotIdAssignmentsIdMutationError = unknown;

/**
 * @summary Update snapshot label assignment
 */
export const usePutLabelsSnapshotsSnapshotIdAssignmentsId = <
  TError = unknown,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof putLabelsSnapshotsSnapshotIdAssignmentsId>>,
      TError,
      { snapshotId: string; id: string },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof putLabelsSnapshotsSnapshotIdAssignmentsId>>,
  TError,
  { snapshotId: string; id: string },
  TContext
> => {
  const mutationOptions =
    getPutLabelsSnapshotsSnapshotIdAssignmentsIdMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Deletes a manually-assigned label with the given id in the snapshot. Only labels whose assignment is manual can be removed here; auto and inherited labels return 404. The removal always applies to the current snapshot. Set optional query param `reflectToRules` to true to also delete the backing manual rule when the row is linked (`ruleId` set); omit or set false to remove only the snapshot assignment row. Ignored on snapshot-only rows.
 * @summary Delete snapshot label assignment
 */
export const deleteLabelsSnapshotsSnapshotIdAssignmentsId = (
  snapshotId: string,
  id: string,
  params?: DeleteLabelsSnapshotsSnapshotIdAssignmentsIdParams,
) => {
  return apiClient<unknown>({
    url: `/labels/snapshots/${snapshotId}/assignments/${id}`,
    method: 'DELETE',
    params,
  });
};

export const getDeleteLabelsSnapshotsSnapshotIdAssignmentsIdMutationOptions = <
  TError = unknown,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteLabelsSnapshotsSnapshotIdAssignmentsId>>,
    TError,
    {
      snapshotId: string;
      id: string;
      params?: DeleteLabelsSnapshotsSnapshotIdAssignmentsIdParams;
    },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteLabelsSnapshotsSnapshotIdAssignmentsId>>,
  TError,
  {
    snapshotId: string;
    id: string;
    params?: DeleteLabelsSnapshotsSnapshotIdAssignmentsIdParams;
  },
  TContext
> => {
  const mutationKey = ['deleteLabelsSnapshotsSnapshotIdAssignmentsId'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteLabelsSnapshotsSnapshotIdAssignmentsId>>,
    {
      snapshotId: string;
      id: string;
      params?: DeleteLabelsSnapshotsSnapshotIdAssignmentsIdParams;
    }
  > = (props) => {
    const { snapshotId, id, params } = props ?? {};

    return deleteLabelsSnapshotsSnapshotIdAssignmentsId(snapshotId, id, params);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteLabelsSnapshotsSnapshotIdAssignmentsIdMutationResult =
  NonNullable<
    Awaited<ReturnType<typeof deleteLabelsSnapshotsSnapshotIdAssignmentsId>>
  >;

export type DeleteLabelsSnapshotsSnapshotIdAssignmentsIdMutationError = unknown;

/**
 * @summary Delete snapshot label assignment
 */
export const useDeleteLabelsSnapshotsSnapshotIdAssignmentsId = <
  TError = unknown,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof deleteLabelsSnapshotsSnapshotIdAssignmentsId>>,
      TError,
      {
        snapshotId: string;
        id: string;
        params?: DeleteLabelsSnapshotsSnapshotIdAssignmentsIdParams;
      },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof deleteLabelsSnapshotsSnapshotIdAssignmentsId>>,
  TError,
  {
    snapshotId: string;
    id: string;
    params?: DeleteLabelsSnapshotsSnapshotIdAssignmentsIdParams;
  },
  TContext
> => {
  const mutationOptions =
    getDeleteLabelsSnapshotsSnapshotIdAssignmentsIdMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Distinct label names assigned within the selected snapshot (queried from live device/interface label assignments). Used by the labels advanced filter to autocomplete "includes name" criteria.
 * @summary Snapshot label names
 */
export const postTablesLabelSnapshotNames = (
  postTablesLabelSnapshotNamesBody: PostTablesLabelSnapshotNamesBody,
  signal?: AbortSignal,
) => {
  return apiClient<PostTablesLabelSnapshotNames200>({
    url: `/tables/label-snapshot-names`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: postTablesLabelSnapshotNamesBody,
    signal,
  });
};

export const getPostTablesLabelSnapshotNamesQueryKey = (
  postTablesLabelSnapshotNamesBody: PostTablesLabelSnapshotNamesBody,
) => {
  return [
    `/tables/label-snapshot-names`,
    postTablesLabelSnapshotNamesBody,
  ] as const;
};

export const getPostTablesLabelSnapshotNamesQueryOptions = <
  TData = Awaited<ReturnType<typeof postTablesLabelSnapshotNames>>,
  TError = unknown,
>(
  postTablesLabelSnapshotNamesBody: PostTablesLabelSnapshotNamesBody,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof postTablesLabelSnapshotNames>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getPostTablesLabelSnapshotNamesQueryKey(postTablesLabelSnapshotNamesBody);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof postTablesLabelSnapshotNames>>
  > = ({ signal }) =>
    postTablesLabelSnapshotNames(postTablesLabelSnapshotNamesBody, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof postTablesLabelSnapshotNames>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type PostTablesLabelSnapshotNamesQueryResult = NonNullable<
  Awaited<ReturnType<typeof postTablesLabelSnapshotNames>>
>;
export type PostTablesLabelSnapshotNamesQueryError = unknown;

export function usePostTablesLabelSnapshotNames<
  TData = Awaited<ReturnType<typeof postTablesLabelSnapshotNames>>,
  TError = unknown,
>(
  postTablesLabelSnapshotNamesBody: PostTablesLabelSnapshotNamesBody,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof postTablesLabelSnapshotNames>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof postTablesLabelSnapshotNames>>,
          TError,
          Awaited<ReturnType<typeof postTablesLabelSnapshotNames>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function usePostTablesLabelSnapshotNames<
  TData = Awaited<ReturnType<typeof postTablesLabelSnapshotNames>>,
  TError = unknown,
>(
  postTablesLabelSnapshotNamesBody: PostTablesLabelSnapshotNamesBody,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof postTablesLabelSnapshotNames>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof postTablesLabelSnapshotNames>>,
          TError,
          Awaited<ReturnType<typeof postTablesLabelSnapshotNames>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function usePostTablesLabelSnapshotNames<
  TData = Awaited<ReturnType<typeof postTablesLabelSnapshotNames>>,
  TError = unknown,
>(
  postTablesLabelSnapshotNamesBody: PostTablesLabelSnapshotNamesBody,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof postTablesLabelSnapshotNames>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Snapshot label names
 */

export function usePostTablesLabelSnapshotNames<
  TData = Awaited<ReturnType<typeof postTablesLabelSnapshotNames>>,
  TError = unknown,
>(
  postTablesLabelSnapshotNamesBody: PostTablesLabelSnapshotNamesBody,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof postTablesLabelSnapshotNames>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getPostTablesLabelSnapshotNamesQueryOptions(
    postTablesLabelSnapshotNamesBody,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Distinct assigned label name/value pairs within the selected snapshot (queried from live device/interface label assignments). Used by the labels advanced filter to autocomplete exact "includes" tokens.
 * @summary Snapshot label pairs
 */
export const postTablesLabelSnapshotPairs = (
  postTablesLabelSnapshotPairsBody: PostTablesLabelSnapshotPairsBody,
  signal?: AbortSignal,
) => {
  return apiClient<PostTablesLabelSnapshotPairs200>({
    url: `/tables/label-snapshot-pairs`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: postTablesLabelSnapshotPairsBody,
    signal,
  });
};

export const getPostTablesLabelSnapshotPairsQueryKey = (
  postTablesLabelSnapshotPairsBody: PostTablesLabelSnapshotPairsBody,
) => {
  return [
    `/tables/label-snapshot-pairs`,
    postTablesLabelSnapshotPairsBody,
  ] as const;
};

export const getPostTablesLabelSnapshotPairsQueryOptions = <
  TData = Awaited<ReturnType<typeof postTablesLabelSnapshotPairs>>,
  TError = unknown,
>(
  postTablesLabelSnapshotPairsBody: PostTablesLabelSnapshotPairsBody,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof postTablesLabelSnapshotPairs>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getPostTablesLabelSnapshotPairsQueryKey(postTablesLabelSnapshotPairsBody);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof postTablesLabelSnapshotPairs>>
  > = ({ signal }) =>
    postTablesLabelSnapshotPairs(postTablesLabelSnapshotPairsBody, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof postTablesLabelSnapshotPairs>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type PostTablesLabelSnapshotPairsQueryResult = NonNullable<
  Awaited<ReturnType<typeof postTablesLabelSnapshotPairs>>
>;
export type PostTablesLabelSnapshotPairsQueryError = unknown;

export function usePostTablesLabelSnapshotPairs<
  TData = Awaited<ReturnType<typeof postTablesLabelSnapshotPairs>>,
  TError = unknown,
>(
  postTablesLabelSnapshotPairsBody: PostTablesLabelSnapshotPairsBody,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof postTablesLabelSnapshotPairs>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof postTablesLabelSnapshotPairs>>,
          TError,
          Awaited<ReturnType<typeof postTablesLabelSnapshotPairs>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function usePostTablesLabelSnapshotPairs<
  TData = Awaited<ReturnType<typeof postTablesLabelSnapshotPairs>>,
  TError = unknown,
>(
  postTablesLabelSnapshotPairsBody: PostTablesLabelSnapshotPairsBody,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof postTablesLabelSnapshotPairs>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof postTablesLabelSnapshotPairs>>,
          TError,
          Awaited<ReturnType<typeof postTablesLabelSnapshotPairs>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function usePostTablesLabelSnapshotPairs<
  TData = Awaited<ReturnType<typeof postTablesLabelSnapshotPairs>>,
  TError = unknown,
>(
  postTablesLabelSnapshotPairsBody: PostTablesLabelSnapshotPairsBody,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof postTablesLabelSnapshotPairs>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Snapshot label pairs
 */

export function usePostTablesLabelSnapshotPairs<
  TData = Awaited<ReturnType<typeof postTablesLabelSnapshotPairs>>,
  TError = unknown,
>(
  postTablesLabelSnapshotPairsBody: PostTablesLabelSnapshotPairsBody,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof postTablesLabelSnapshotPairs>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getPostTablesLabelSnapshotPairsQueryOptions(
    postTablesLabelSnapshotPairsBody,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Lists every assigned device and interface label (manual, auto, inherited and ipf) within a snapshot as a single paginated collection. Each row carries a type discriminator and its assignment source. Manual rows can be edited or removed in place through the per-row label endpoints.
 * @summary Snapshot label assignments
 */
export const postTablesLabels = (
  postTablesLabelsBody: PostTablesLabelsBody,
  signal?: AbortSignal,
) => {
  return apiClient<PostTablesLabels200>({
    url: `/tables/labels`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: postTablesLabelsBody,
    signal,
  });
};

export const getPostTablesLabelsQueryKey = (
  postTablesLabelsBody: PostTablesLabelsBody,
) => {
  return [`/tables/labels`, postTablesLabelsBody] as const;
};

export const getPostTablesLabelsQueryOptions = <
  TData = Awaited<ReturnType<typeof postTablesLabels>>,
  TError = unknown,
>(
  postTablesLabelsBody: PostTablesLabelsBody,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof postTablesLabels>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getPostTablesLabelsQueryKey(postTablesLabelsBody);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof postTablesLabels>>
  > = ({ signal }) => postTablesLabels(postTablesLabelsBody, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof postTablesLabels>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type PostTablesLabelsQueryResult = NonNullable<
  Awaited<ReturnType<typeof postTablesLabels>>
>;
export type PostTablesLabelsQueryError = unknown;

export function usePostTablesLabels<
  TData = Awaited<ReturnType<typeof postTablesLabels>>,
  TError = unknown,
>(
  postTablesLabelsBody: PostTablesLabelsBody,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof postTablesLabels>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof postTablesLabels>>,
          TError,
          Awaited<ReturnType<typeof postTablesLabels>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function usePostTablesLabels<
  TData = Awaited<ReturnType<typeof postTablesLabels>>,
  TError = unknown,
>(
  postTablesLabelsBody: PostTablesLabelsBody,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof postTablesLabels>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof postTablesLabels>>,
          TError,
          Awaited<ReturnType<typeof postTablesLabels>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function usePostTablesLabels<
  TData = Awaited<ReturnType<typeof postTablesLabels>>,
  TError = unknown,
>(
  postTablesLabelsBody: PostTablesLabelsBody,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof postTablesLabels>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Snapshot label assignments
 */

export function usePostTablesLabels<
  TData = Awaited<ReturnType<typeof postTablesLabels>>,
  TError = unknown,
>(
  postTablesLabelsBody: PostTablesLabelsBody,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof postTablesLabels>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getPostTablesLabelsQueryOptions(
    postTablesLabelsBody,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  query.queryKey = queryOptions.queryKey;

  return query;
}
